import { useState } from "react"
import { Layout } from "../components/Layout"
import { useProductStats } from "@/hooks/useProductStats"
import type {
  FunnelCard,
  MixCard,
  PlatformFunnelsCard,
  PlatformVolumesCard,
} from "@/hooks/useProductStats"
import { CardSkeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import {
  ExclamationTriangleIcon,
  FunnelIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"
import {
  fmtInt,
  fmtPct,
  humanizeEvent,
  humanizePlatform,
  coverageSummary,
} from "@/lib/productStatsFormat"

const WINDOWS = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
]

// A section whose source query failed — shown instead of empty/data.
function Unavailable({ what, error }: { what: string; error?: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
      <span>
        {what} unavailable{error ? `: ${error}` : ""}
      </span>
    </div>
  )
}

function Funnel({ title, card }: { title: string; card: FunnelCard }) {
  if (!card.ok) return <Unavailable what={`${title} funnel`} error={card.error} />
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-secondary">
          <th className="py-1 pr-4 font-medium">Step</th>
          <th className="py-1 pr-4 font-medium">Persons</th>
          <th className="py-1 pr-4 font-medium">From prev</th>
          <th className="py-1 font-medium">From start</th>
        </tr>
      </thead>
      <tbody>
        {card.steps.map((s, i) => (
          <tr key={s.event} className="border-t border-border/50">
            <td className="py-1 pr-4 text-primary">
              <span className="text-secondary">{i + 1}.</span> {humanizeEvent(s.event)}
            </td>
            <td className="py-1 pr-4 text-primary">{fmtInt(s.persons)}</td>
            <td className="py-1 pr-4 text-secondary">{i === 0 ? "—" : fmtPct(s.conversionFromPrev)}</td>
            <td className="py-1 text-primary">{i === 0 ? "—" : fmtPct(s.conversionFromStart)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// #1500 — the same funnel, one copy per `platform`, so web / iOS / chopper stop being averaged
// into one number. Only platforms with events in the window get a column; the note under it says
// why a chopper figure is a floor rather than a total.
function PlatformFunnels({ card }: { card: PlatformFunnelsCard }) {
  if (!card.ok) return <Unavailable what="Upload by platform" error={card.error} />
  return (
    <div className="space-y-4">
      {card.platforms.length === 0 ? (
        <p className="text-sm text-secondary">No platform-tagged upload events in window.</p>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {card.platforms.map((p) => (
            <div key={p.platform}>
              <h4 className="mb-1 text-sm font-medium text-primary">
                {humanizePlatform(p.platform)}
              </h4>
              <table className="w-full text-sm">
                <tbody>
                  {p.steps.map((s, i) => (
                    <tr key={s.event} className="border-t border-border/50">
                      <td className="py-1 pr-4 text-primary">
                        <span className="text-secondary">{i + 1}.</span> {humanizeEvent(s.event)}
                      </td>
                      <td className="py-1 pr-4 text-primary">{fmtInt(s.persons)}</td>
                      <td className="py-1 text-secondary">
                        {i === 0 ? "—" : fmtPct(s.conversionFromStart)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-secondary">{card.platformNote}</p>
    </div>
  )
}

// #1500 — key-event volumes split by `platform`. Persons is a distinct-person count per platform,
// so it is deliberately NOT summed across platforms (that would double-count a multi-client user);
// the blended card above is where the correct total lives.
function VolumesByPlatform({ card }: { card: PlatformVolumesCard }) {
  if (!card.ok) return <Unavailable what="Event volumes by platform" error={card.error} />
  if (card.events.length === 0) {
    return <p className="text-sm text-secondary">No key events in window.</p>
  }
  return (
    <div className="space-y-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-secondary">
            <th className="py-1 pr-4 font-medium">Event</th>
            <th className="py-1 pr-4 font-medium">Platform</th>
            <th className="py-1 pr-4 font-medium">Occurrences</th>
            <th className="py-1 font-medium">Persons</th>
          </tr>
        </thead>
        <tbody>
          {card.events.flatMap((e) =>
            e.platforms.map((p, i) => (
              <tr key={`${e.event}:${p.platform}`} className="border-t border-border/50">
                <td className="py-1 pr-4 text-primary">{i === 0 ? humanizeEvent(e.event) : ""}</td>
                <td className="py-1 pr-4 text-secondary">{humanizePlatform(p.platform)}</td>
                <td className="py-1 pr-4 text-primary">{fmtInt(p.occurrences)}</td>
                <td className="py-1 text-secondary">{fmtInt(p.persons)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p className="text-xs text-secondary">{card.platformNote}</p>
    </div>
  )
}

function Mix({ title, card }: { title: string; card: MixCard }) {
  if (!card.ok) return <Unavailable what={`${title} mix`} error={card.error} />
  if (card.slices.length === 0) {
    return <p className="text-sm text-secondary">No data in window.</p>
  }
  const total = card.slices.reduce((a, s) => a + s.persons, 0)
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-secondary">{title}</h3>
      <table className="w-full text-sm">
        <tbody>
          {card.slices.map((s) => (
            <tr key={s.value} className="border-t border-border/50">
              <td className="py-1 pr-4 text-primary">{s.value}</td>
              <td className="py-1 pr-4 text-primary">{fmtInt(s.persons)}</td>
              <td className="py-1 text-secondary">{fmtPct(total ? (s.persons / total) * 100 : 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ProductStats() {
  const [windowDays, setWindowDays] = useState(90)
  const { data, isLoading, error } = useProductStats(windowDays)

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">Product stats</h1>
            <p className="text-secondary">
              Funnels, key-event volumes &amp; consent coverage (PostHog, server-side)
              {data
                ? ` · ${data.environment} · last ${data.window_days}d · updated ${new Date(
                    data.generated_at
                  ).toLocaleTimeString()}`
                : ""}
            </p>
          </div>
          <div className="flex gap-1 rounded-md border border-border p-1">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                onClick={() => setWindowDays(w.days)}
                className={`rounded px-3 py-1 text-sm ${
                  windowDays === w.days
                    ? "bg-accent text-accent-foreground"
                    : "text-secondary hover:text-primary"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading || !data ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : !data.configured ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
            <span>
              Product stats are not configured. Set the PostHog personal API key secret:{" "}
              <code className="font-mono">wrangler secret put POSTHOG_PERSONAL_API_KEY</code> (dev +
              prod). The key is held server-side only and never reaches this page.
            </span>
          </div>
        ) : (
          <>
            {/* Activation + upload funnels */}
            <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-5 w-5 text-secondary" />
                <h2 className="text-lg font-semibold text-primary">Funnels</h2>
              </div>
              <div>
                <h3 className="mb-2 text-sm font-medium text-secondary">Activation</h3>
                <Funnel title="Activation" card={data.activation} />
              </div>
              <div>
                <h3 className="mb-1 text-sm font-medium text-secondary">
                  Upload — all platforms combined
                </h3>
                <p className="mb-2 text-xs text-secondary">
                  Web + iOS + chopper + backend blended together, including the server-side
                  transcode leg. For &quot;who is uploading&quot;, read the per-platform split
                  below instead.
                </p>
                <Funnel title="Upload" card={data.upload} />
              </div>
              <div>
                <h3 className="mb-1 text-sm font-medium text-secondary">Upload by platform</h3>
                <p className="mb-2 text-xs text-secondary">
                  Client-emitted steps only. <code className="font-mono">transcode_started</code>{" "}
                  is omitted here because it is emitted server-side and is always{" "}
                  <code className="font-mono">platform=backend</code>, so it cannot be attributed
                  to the client that started the upload.
                </p>
                <PlatformFunnels card={data.uploadByPlatform} />
              </div>
            </section>

            {/* Key-event volumes */}
            <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
              <div className="flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 text-secondary" />
                <h2 className="text-lg font-semibold text-primary">Key event volumes</h2>
              </div>
              {!data.volumes.ok ? (
                <Unavailable what="Event volumes" error={data.volumes.error} />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-secondary">
                      <th className="py-1 pr-4 font-medium">Event</th>
                      <th className="py-1 pr-4 font-medium">Occurrences</th>
                      <th className="py-1 font-medium">Persons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.volumes.events.map((e) => (
                      <tr key={e.event} className="border-t border-border/50">
                        <td className="py-1 pr-4 text-primary">{humanizeEvent(e.event)}</td>
                        <td className="py-1 pr-4 text-primary">{fmtInt(e.occurrences)}</td>
                        <td className="py-1 text-secondary">{fmtInt(e.persons)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div>
                <h3 className="mb-2 text-sm font-medium text-secondary">By platform</h3>
                <VolumesByPlatform card={data.volumesByPlatform} />
              </div>
            </section>

            {/* Device / OS / role mix */}
            <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
              <div className="flex items-center gap-2">
                <DevicePhoneMobileIcon className="h-5 w-5 text-secondary" />
                <h2 className="text-lg font-semibold text-primary">Device / OS / role mix</h2>
              </div>
              <div className="grid gap-6 md:grid-cols-3">
                <Mix title="Device" card={data.deviceRoleMix.device} />
                <Mix title="OS" card={data.deviceRoleMix.os} />
                <Mix title="Role" card={data.deviceRoleMix.role} />
              </div>
            </section>

            {/* Compliance coverage */}
            <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 text-secondary" />
                <h2 className="text-lg font-semibold text-primary">Compliance coverage</h2>
              </div>
              {!data.coverage.ok ? (
                <Unavailable what="Coverage" error={data.coverage.error} />
              ) : data.coverage.rows.length === 0 ? (
                <p className="text-sm text-secondary">No events with capture-mode data in window.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.coverage.rows.map((row) => (
                    <li key={row.event} className="border-t border-border/50 py-1 text-primary">
                      {coverageSummary(row)}{" "}
                      <span className="text-secondary">({fmtInt(row.total)} events)</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-secondary">{data.coverage.cfDenominatorNote}</p>
            </section>
          </>
        )}
      </div>
    </Layout>
  )
}
